/**
 * @fileoverview Componente responsável por organizar itens em um quadro kanban.
 */

import React, { useState, useMemo } from "react";
import { makeStyles } from "@material-ui/core/styles";
import Typography from "@material-ui/core/Typography";
import Tooltip from "@material-ui/core/Tooltip";
import { toast } from "react-toastify";

import KanbanColumn from "./KanbanColumn";
import LeadStatusModal from "../LeadStatusModal";
import ConfirmationModal from "../ConfirmationModal";
import api from "../../services/api";

import { Plus, LayoutGrid } from "lucide-react";

const useStyles = makeStyles((theme) => {
  const dark = theme.palette.type === "dark";
  return {
    boardContainer: {
      display: "flex",
      gap: 12,
      overflowX: "auto",
      overflowY: "hidden",
      flex: 1,
      padding: "4px 4px 12px 2px",
      alignItems: "flex-start",
      "&::-webkit-scrollbar": { height: 10 },
      "&::-webkit-scrollbar-track": {
        background: "transparent",
      },
      "&::-webkit-scrollbar-thumb": {
        background: dark ? "rgba(255,255,255,0.06)" : "rgba(15,23,42,0.08)",
        borderRadius: 5,
        border: dark ? "2px solid #0A0A0A" : "2px solid #F8FAFC",
      },
      "&::-webkit-scrollbar-thumb:hover": {
        background: dark ? "rgba(255,255,255,0.12)" : "rgba(15,23,42,0.18)",
      },
    },
    addColumnBtn: {
      flexShrink: 0,
      width: 296,
      minWidth: 296,
      height: 96,
      borderRadius: 12,
      border: dark
        ? "1px dashed rgba(255,255,255,0.08)"
        : "1px dashed rgba(15,23,42,0.14)",
      background: "transparent",
      color: dark ? "#9CA3AF" : "#64748B",
      transition: "border-color .15s, color .15s, background .15s",
      cursor: "pointer",
      display: "flex",
      flexDirection: "column",
      alignItems: "center",
      justifyContent: "center",
      gap: 6,
      fontFamily: "inherit",
      fontSize: 13,
      fontWeight: 600,
      "&:hover": {
        borderColor: dark ? "rgba(99,102,241,0.28)" : "rgba(61,92,253,0.32)",
        color: dark ? "#A5B4FC" : "#3D5CFD",
        background: dark ? "rgba(99,102,241,0.03)" : "rgba(61,92,253,0.03)",
      },
    },
    addColumnLabel: {
      fontSize: 12.5,
      fontWeight: 600,
      letterSpacing: "0.01em",
    },
    emptyBoard: {
      display: "flex",
      flexDirection: "column",
      alignItems: "center",
      justifyContent: "center",
      gap: 8,
      padding: theme.spacing(6),
      flex: 1,
      color: dark ? "#9CA3AF" : "#94A3B8",
      textAlign: "center",
    },
    emptyBoardIcon: {
      width: 64,
      height: 64,
      borderRadius: 16,
      display: "inline-flex",
      alignItems: "center",
      justifyContent: "center",
      background: dark ? "rgba(255,255,255,0.04)" : "rgba(15,23,42,0.04)",
      color: dark ? "#9CA3AF" : "#64748B",
      marginBottom: 8,
    },
    emptyBoardTitle: {
      fontSize: "1rem",
      fontWeight: 600,
      color: dark ? "#E8EAED" : "#0F172A",
    },
    emptyBoardHint: {
      fontSize: 13,
      maxWidth: 360,
      lineHeight: 1.5,
    },
  };
});

const KanbanBoard = ({
  statuses,
  boardLeads,
  searchParam,
  assignedUserFilter,
  onDropCard,
  onReorderColumns,
  onViewLead,
  onDeleteLead,
  onStartConversation,
  onAddLead,
  onStatusesChange,
}) => {
  const classes = useStyles();
  const [statusModalOpen, setStatusModalOpen] = useState(false);
  const [draggingColumnId, setDraggingColumnId] = useState(null);
  const [dragOverColumnId, setDragOverColumnId] = useState(null);
  const [deletingStatusId, setDeletingStatusId] = useState(null);

  // Filter leads by search and assignee across all columns
  const filteredBoardLeads = useMemo(() => {
    if (!searchParam && !assignedUserFilter) return boardLeads;

    const filtered = {};
    const search = searchParam ? searchParam.toLowerCase() : "";

    for (const [statusId, leads] of Object.entries(boardLeads)) {
      filtered[statusId] = leads.filter(lead => {
        // Assignee filter
        if (assignedUserFilter) {
          if (assignedUserFilter === "unassigned") {
            if (lead.assignedUserId) return false;
          } else {
            if (lead.assignedUserId !== assignedUserFilter) return false;
          }
        }

        // Search filter
        if (!search) return true;
        if ((lead.name || '').toLowerCase().includes(search)) return true;
        if ((lead.phone || '').toLowerCase().includes(search)) return true;
        if ((lead.email || '').toLowerCase().includes(search)) return true;
        if ((lead.cpf || '').toLowerCase().includes(search)) return true;
        if ((lead.serviceOrder || '').toLowerCase().includes(search)) return true;
        // Dynamic fields (new card format)
        if (lead.customFields?.fields) {
          return lead.customFields.fields.some(f =>
            (f.value?.toString() || '').toLowerCase().includes(search) ||
            (f.label || '').toLowerCase().includes(search)
          );
        }
        // Legacy flat customFields (WordPress)
        if (lead.customFields && !lead.customFields.fields) {
          return Object.values(lead.customFields).some(v =>
            (v?.toString() || '').toLowerCase().includes(search)
          );
        }
        return false;
      });
    }
    return filtered;
  }, [boardLeads, searchParam, assignedUserFilter]);

  const sortedStatuses = useMemo(() => {
    return [...statuses].sort((a, b) => a.order - b.order);
  }, [statuses]);

  // Column reorder handlers
  const handleDragColumnStart = (columnId) => {
    setDraggingColumnId(columnId);
  };

  const handleDragColumnOver = (columnId) => {
    if (draggingColumnId && draggingColumnId !== columnId) {
      setDragOverColumnId(columnId);
    }
  };

  const handleDragColumnDrop = (fromColumnId, toColumnId) => {
    if (fromColumnId === toColumnId) return;

    // Reorder: move fromColumnId to toColumnId's position
    const ordered = sortedStatuses.map(s => s.id);
    const fromIndex = ordered.indexOf(fromColumnId);
    const toIndex = ordered.indexOf(toColumnId);
    if (fromIndex === -1 || toIndex === -1) return;

    ordered.splice(fromIndex, 1);
    ordered.splice(toIndex, 0, fromColumnId);

    onReorderColumns(ordered);
    setDraggingColumnId(null);
    setDragOverColumnId(null);
  };

  const handleDragEnd = () => {
    setDraggingColumnId(null);
    setDragOverColumnId(null);
  };

  const handleEditColumn = async (statusId, newName) => {
    try {
      await api.put(`/lead-statuses/${statusId}`, { name: newName });
      if (onStatusesChange) onStatusesChange();
    } catch (err) {
      toast.error("Erro ao renomear coluna");
    }
  };

  const handleDeleteColumn = async () => {
    if (!deletingStatusId) return;
    try {
      await api.delete(`/lead-statuses/${deletingStatusId}`);
      toast.success("Coluna excluída");
      if (onStatusesChange) onStatusesChange();
    } catch (err) {
      toast.error("Erro ao excluir coluna");
    }
    setDeletingStatusId(null);
  };

  if (sortedStatuses.length === 0) {
    return (
      <div className={classes.emptyBoard}>
        <div className={classes.emptyBoardIcon}>
          <LayoutGrid size={28} strokeWidth={1.5} />
        </div>
        <Typography className={classes.emptyBoardTitle}>
          Nenhum status configurado
        </Typography>
        <Typography className={classes.emptyBoardHint}>
          Abra as configurações no topo direito para criar etapas do seu funil.
        </Typography>
      </div>
    );
  }

  return (
    <>
      <ConfirmationModal
        title="Excluir coluna"
        open={Boolean(deletingStatusId)}
        onClose={() => setDeletingStatusId(null)}
        onConfirm={handleDeleteColumn}
      >
        Tem certeza que deseja excluir esta coluna? Os leads associados não serão excluídos.
      </ConfirmationModal>

      <div
        className={classes.boardContainer}
        onDragEnd={handleDragEnd}
      >
        {sortedStatuses.map((status) => (
          <KanbanColumn
            key={status.id}
            status={status}
            leads={filteredBoardLeads[status.id] || []}
            onDropCard={onDropCard}
            onDragColumnStart={handleDragColumnStart}
            onDragColumnOver={handleDragColumnOver}
            onDragColumnDrop={handleDragColumnDrop}
            onViewLead={onViewLead}
            onDeleteLead={onDeleteLead}
            onStartConversation={onStartConversation}
            onAddLead={onAddLead}
            onEditColumn={handleEditColumn}
            onDeleteColumn={(id) => setDeletingStatusId(id)}
            isDraggingColumn={draggingColumnId === status.id}
          />
        ))}

        <Tooltip title="Adicionar nova coluna ao quadro">
          <button
            type="button"
            className={classes.addColumnBtn}
            onClick={() => setStatusModalOpen(true)}
            aria-label="Nova coluna"
          >
            <Plus size={18} strokeWidth={1.75} />
            <span className={classes.addColumnLabel}>Nova coluna</span>
          </button>
        </Tooltip>
      </div>

      <LeadStatusModal
        open={statusModalOpen}
        onClose={() => {
          setStatusModalOpen(false);
          if (onStatusesChange) onStatusesChange();
        }}
      />
    </>
  );
};

export default KanbanBoard;
